import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PhoneCall, ArrowRight, ShieldAlert, RotateCcw, CheckCircle2, Loader2, Sparkles, ChevronsLeftRight, Hand } from "lucide-react";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Textarea } from "./ui/textarea";
import { acknowledgeDisclaimer, conceptUrl, getConcepts, photoUrl, requestRevision } from "../lib/api";
import MoreViews, { FailedConceptNotice } from "./MoreViews";
import PortfolioConsent from "./PortfolioConsent";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "—");

function Label({ children, tone }) {
  return <span className={`absolute top-3 ${tone === "after" ? "right-3 bg-emerald-600" : "left-3 bg-black/65"} text-white text-[11px] font-bold tracking-wider rounded-full px-2.5 py-1`}>{children}</span>;
}

const VIEWS = [["Before", 100], ["Compare", 50], ["After", 0]];

function BeforeAfter({ before, after }) {
  const [pos, setPos] = useState(50);
  return (
    <div className="space-y-3">
      <div data-testid="before-after-instructions" className="flex items-start gap-2 rounded-xl bg-emerald-50 border border-emerald-100 px-3 py-2.5 text-sm text-emerald-900">
        <Hand size={18} className="mt-0.5 shrink-0 text-emerald-700" />
        <span><strong>Slide to compare:</strong> drag the white handle left and right across the photo to see your property before and after. Or tap Before / After below.</span>
      </div>
      <div className="relative rounded-2xl overflow-hidden bg-slate-900 select-none" data-testid="before-after-slider" style={{ touchAction: "pan-y" }}>
        <img src={before} alt="Before — your original photo" className="block w-full h-auto" />
        <img data-testid="concept-image" src={after} alt="GreenFlow concept" className="absolute inset-0 w-full h-full object-cover" style={{ clipPath: `inset(0 0 0 ${pos}%)` }} />
        <div className={`absolute inset-y-0 w-1 -ml-0.5 bg-white shadow-lg pointer-events-none ${pos === 0 || pos === 100 ? "hidden" : ""}`} style={{ left: `${pos}%` }}>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white shadow-xl flex items-center justify-center text-emerald-700">
            <ChevronsLeftRight size={22} />
          </div>
        </div>
        {pos > 8 && <Label>BEFORE</Label>}
        {pos < 92 && <Label tone="after">GREENFLOW CONCEPT</Label>}
        <input
          type="range" min="0" max="100" value={pos} onChange={(e) => setPos(Number(e.target.value))}
          aria-label="Compare before and GreenFlow concept" data-testid="before-after-range"
          className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize"
        />
      </div>
      <div className="grid grid-cols-3 gap-2" data-testid="before-after-toggle">
        {VIEWS.map(([label, v]) => (
          <button
            key={label} type="button" onClick={() => setPos(v)} data-testid={`before-after-${label.toLowerCase()}-button`}
            className={`h-11 rounded-xl text-sm font-semibold border transition-colors ${pos === v ? "bg-[#092C15] text-white border-[#092C15]" : "bg-white text-emerald-800 border-emerald-200 hover:bg-emerald-50"}`}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

function Detail({ label, children, testId }) {
  return (
    <div data-testid={testId} className="py-2.5 border-b border-slate-100 last:border-0">
      <div className="text-[11px] uppercase tracking-widest font-bold text-emerald-700">{label}</div>
      <div className="text-sm text-slate-700 mt-0.5">{children}</div>
    </div>
  );
}

export function Revision({ session, setSession, options }) {
  const [open, setOpen] = useState(false);
  const [choice, setChoice] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const rev = session.revision_request;
  if (rev) {
    return (
      <div data-testid="revision-recorded" className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-900 flex gap-2">
        <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
        <span>Revision request recorded for GreenFlow review: <strong>{rev.adjustment}</strong>{rev.comment ? ` — ${rev.comment}` : ""}</span>
      </div>
    );
  }
  const submit = async () => {
    setBusy(true);
    try {
      setSession(await requestRevision(session.id, choice, comment));
      toast.success("Revision request saved for GreenFlow review.");
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Could not save your revision request.");
    } finally {
      setBusy(false);
    }
  };
  if (!open) {
    return (
      <Button data-testid="request-revision-button" variant="outline" onClick={() => setOpen(true)} className="w-full h-11 rounded-xl border-emerald-300 text-emerald-800 font-semibold">
        <RotateCcw size={15} className="mr-2" /> REQUEST ONE REVISION
      </Button>
    );
  }
  return (
    <div data-testid="revision-panel" className="rounded-2xl border border-emerald-200 bg-white p-4">
      <div className="font-semibold text-[#092C15]">Choose one main adjustment</div>
      <div className="mt-2 flex flex-wrap gap-2">
        {(options || []).map((o) => (
          <button key={o} type="button" data-testid={`revision-option-${slug(o)}`} onClick={() => setChoice(o)}
            className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${choice === o ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-slate-200 text-slate-700"}`}>
            {o === "Other" ? "Other (add a comment)" : o}
          </button>
        ))}
      </div>
      <Textarea data-testid="revision-comment-input" rows={2} maxLength={500} className="mt-3" placeholder="Short comment (required for Other)" value={comment} onChange={(e) => setComment(e.target.value)} />
      <p className="mt-2 text-xs text-slate-500">Your revision request is reviewed by GreenFlow before any new concept is created.</p>
      <Button data-testid="submit-revision-button" disabled={!choice || busy} onClick={submit} className="mt-3 w-full h-11 rounded-xl bg-emerald-600 hover:bg-emerald-700 font-semibold">
        {busy && <Loader2 size={15} className="mr-2 animate-spin" />} Submit Revision Request
      </Button>
    </div>
  );
}

export default function ConceptResults({ session, setSession, cfg, children }) {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const c0 = session.concept;
  const a = session.assessment || {};
  const i = session.intake || {};
  const primary = (session.photos || []).find((p) => p.is_primary) || session.photos?.[0];
  const acked = !!session.disclaimer_ack?.accepted;
  const [data, setData] = useState(null);
  const [selId, setSelId] = useState(c0.id);
  const refresh = useCallback(() => getConcepts(session.id).then(setData).catch(() => {}), [session.id]);
  const pending = (data?.concepts || []).some((x) => ["queued", "processing"].includes(x.status));
  useEffect(() => {
    refresh();
  }, [refresh]);
  useEffect(() => {
    if (!pending) return undefined;
    const t = setInterval(refresh, 4000);
    return () => clearInterval(t);
  }, [pending, refresh]);
  const all = data?.concepts || [];
  const done = all.filter((x) => x.status === "complete");
  const c = done.find((x) => x.id === selId) || { ...c0, source_photo_id: primary?.id };
  const beforeId = c.source_photo_id || primary?.id;
  const ack = async () => {
    if (acked) return;
    setSaving(true);
    try {
      setSession(await acknowledgeDisclaimer(session.id));
    } catch {
      toast.error("Could not save your acknowledgment.");
    } finally {
      setSaving(false);
    }
  };
  const status = session.revision_request ? "Revision requested — pending GreenFlow review" : "Concept ready";
  return (
    <div className="space-y-4" data-testid="concept-results">
      <div>
        <div className="text-[11px] uppercase tracking-widest font-bold text-emerald-700 flex items-center gap-1.5"><Sparkles size={12} /> Improve My Landscape</div>
        <h2 data-testid="concept-results-title" className="font-display text-2xl sm:text-3xl font-extrabold text-[#092C15] mt-1">YOUR GREENFLOW LANDSCAPE VISION</h2>
      </div>
      <BeforeAfter before={beforeId ? photoUrl(beforeId) : ""} after={conceptUrl(c.id)} />
      {(all.length > 1 || pending) && (
        <div data-testid="concept-gallery" className="flex gap-2 overflow-x-auto pb-1">
          {all.map((x) => (
            <button key={x.id} type="button" disabled={x.status !== "complete"} data-testid={`concept-thumb-${x.version}`} onClick={() => setSelId(x.id)}
              className={`shrink-0 w-24 rounded-lg overflow-hidden border-2 text-[10px] font-semibold ${x.id === c.id ? "border-emerald-600" : "border-slate-200"}`}>
              {x.status === "complete" ? <img src={conceptUrl(x.id)} alt={`Version ${x.version}`} className="aspect-square w-full object-cover" />
                : <div className="aspect-square grid place-items-center bg-slate-100 text-slate-500">{x.status === "error" ? "Failed" : <Loader2 size={16} className="animate-spin" />}</div>}
              <div className="py-0.5 bg-white">v{x.version}{x.parent_concept_id ? " · Revision" : x.kind === "free" ? " · Free" : " · Extra view"}</div>
            </button>
          ))}
        </div>
      )}
      {pending && <p data-testid="concept-creating-note" className="text-xs text-slate-500">Your new concept is being created — this can take up to a minute.</p>}
      {all.filter((x) => x.status === "error" && x.kind !== "free").map((x) => <FailedConceptNotice key={x.id} concept={x} onRetried={refresh} />)}

      <section data-testid="concept-disclaimer" className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-4">
        <div className="flex gap-2 text-sm text-amber-950">
          <ShieldAlert size={18} className="mt-0.5 shrink-0 text-amber-600" />
          <p>{cfg?.disclaimer}</p>
        </div>
        <label className="mt-3 flex items-start gap-2.5 text-sm font-semibold text-[#092C15] cursor-pointer">
          <Checkbox data-testid="disclaimer-ack-checkbox" checked={acked} disabled={acked || saving} onCheckedChange={(v) => v && ack()} className="mt-0.5" />
          I understand this is a conceptual visualization only.
        </label>
      </section>

      <section className="rounded-2xl bg-white border border-slate-200 p-5">
        <Detail testId="concept-direction" label="GreenFlow's recommended direction">{a.recommended_direction}</Detail>
        <Detail testId="concept-categories" label="Enhancement categories included">{(a.opportunities || []).map((o) => o.category).join(", ") || "—"}</Detail>
        <Detail testId="concept-preserve" label="Elements you asked to preserve">{[i.preserve_areas, i.preserve_features].filter(Boolean).join(" · ") || "Nothing specified"}</Detail>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3">
          <Detail testId="concept-date" label="Created">{fmt(c.created_at)}</Detail>
          <Detail testId="concept-version" label="Version">{c.version || 1}</Detail>
          <div className="col-span-2 sm:col-span-1"><Detail testId="concept-status" label="Status">{status}</Detail></div>
        </div>
      </section>

      {data && <MoreViews session={session} setSession={setSession} data={data} refresh={refresh} />}

      {children}

      <section data-testid="vision-cta" className="rounded-3xl p-5 text-white gf-gradient-dark">
        <h3 className="font-display text-2xl font-bold">READY TO BRING THIS VISION TO LIFE?</h3>
        <p className="mt-1 text-emerald-100/80 text-sm">Send your complete project to GreenFlow for review. This is a request — not a confirmed appointment.</p>
        <PortfolioConsent session={session} setSession={setSession} />
        <Button data-testid="vision-consultation-button" disabled={!acked} onClick={() => navigate("/vision")}
          className="mt-4 w-full min-h-12 h-auto py-3 whitespace-normal text-sm sm:text-base bg-white text-[#092C15] hover:bg-emerald-50 font-bold tracking-wide rounded-xl">
          <PhoneCall className="mr-2" size={18} /> REQUEST A GREENFLOW CONSULTATION <ArrowRight className="ml-2" size={18} />
        </Button>
        {!acked && <p data-testid="ack-required-hint" className="mt-2 text-xs text-emerald-100/70 text-center">Please acknowledge the disclaimer above to continue.</p>}
      </section>
    </div>
  );
}
